import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { MapPin, Maximize2, HeartHandshake, ChevronDown, ChevronUp } from 'lucide-react';
import { BUILDING_STATES } from '../map/buildingIcons';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import GpsFreshness from '../GpsFreshness';
import { severityColors, vehicleStatusColors, vehicleStatusLabels } from './dashboardShared';
import DrawingsLayer from '../map/DrawingsLayer';
import CareMarkers from '../map/CareMarkers';
import BuildingsLayer from '../map/BuildingsLayer';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Swatch = ({ color, label, round }) => (
    <span className="w-2.5 h-2.5 inline-block shrink-0" title={label}
        style={{ backgroundColor: color, borderRadius: round ? '50%' : 0, outline: '1px solid rgba(143,153,168,.6)' }} />
);

const LegendRow = ({ title, items, round }) => (
    <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider text-zinc-400 w-16 shrink-0">{title}</span>
        {items.map(([color, label]) => <Swatch key={label} color={color} label={label} round={round} />)}
    </div>
);

// Legende repliee par defaut sur la petite carte ; details au survol des pastilles
const CompactLegend = ({ hasZones }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="absolute bottom-3 left-3 z-[1000] bg-zinc-900 border border-zinc-800 px-2 py-1.5" data-testid="map-legend">
            <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-[11px] font-semibold text-zinc-300" data-testid="map-legend-toggle">
                Légende {open ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            </button>
            {open && (
                <div className="mt-1.5 space-y-1" data-testid="map-legend-content">
                    <LegendRow title="Incidents" round items={[['#c0473e', 'Incident critique'], ['#bf8222', 'Incident modéré']]} />
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400 w-16 shrink-0">CARE</span>
                        <HeartHandshake className="w-3 h-3 text-blue-400" />
                    </div>
                    <LegendRow title="Bâtiments" items={BUILDING_STATES.map((s) => [s.color, s.label])} />
                    <LegendRow title="Véhicules" round items={Object.entries(vehicleStatusColors).map(([k, c]) => [c, vehicleStatusLabels[k]])} />
                    {hasZones && <LegendRow title="Zones" round items={[['#c0473e', 'Zone dangereuse'], ['#3d8a5c', 'Zone sécurisée']]} />}
                </div>
            )}
        </div>
    );
};

const SituationMapCard = ({ theme, mapCenter, incidents, careCenters, vehicles, dangerZones, drawings = [], onFullscreen, zoom = 8 }) => {
    const gpsVehicles = useMemo(() => vehicles.filter(v => v.gps_latitude && v.gps_longitude), [vehicles]);
    const dzSignale = useMemo(() => dangerZones.filter(z => z.latitude && z.longitude && z.statut === 'signale'), [dangerZones]);
    const dzSecurise = useMemo(() => dangerZones.filter(z => z.latitude && z.longitude && z.statut === 'securise'), [dangerZones]);
    return (
    <Card className="lg:col-span-2 content-card" data-testid="map-card">
        <CardHeader className="card-header flex flex-row items-center justify-between">
            <CardTitle className="card-title flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-500" />
                Carte de situation
            </CardTitle>
            <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-zinc-400 hover:text-white"
                onClick={onFullscreen} data-testid="dashboard-fullscreen-map-btn">
                <Maximize2 className="w-3.5 h-3.5 mr-1" /> Plein ecran
            </Button>
        </CardHeader>
        <CardContent className="p-0">
            <div className="h-[250px] md:h-[400px] relative">
                <MapContainer center={mapCenter} zoom={zoom} className="h-full w-full" style={{ background: theme === 'light' ? '#f4f4f5' : '#09090b' }}>
                    <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />

                    {/* Incidents */}
                    {incidents.map((incident) => (
                        <CircleMarker
                            key={incident.id}
                            center={[incident.latitude, incident.longitude]}
                            radius={12}
                            pathOptions={{
                                color: severityColors[incident.severity],
                                fillColor: severityColors[incident.severity],
                                fillOpacity: 0.6,
                                weight: 2
                            }}
                        >
                            <Popup>
                                <div className="p-2">
                                    <h3 className="font-semibold">{incident.title}</h3>
                                    <p className="text-sm">{incident.type} - {incident.severity}</p>
                                </div>
                            </Popup>
                        </CircleMarker>
                    ))}

                    {/* CARE Centers */}
                    <CareMarkers centers={careCenters} />
                    <BuildingsLayer />

                    {/* Vehicles with GPS */}
                    {gpsVehicles.map((vehicle) => (
                        <CircleMarker
                            key={`v-${vehicle.id}`}
                            center={[vehicle.gps_latitude, vehicle.gps_longitude]}
                            radius={8}
                            pathOptions={{
                                color: vehicleStatusColors[vehicle.status] || '#3b82f6',
                                fillColor: vehicleStatusColors[vehicle.status] || '#3b82f6',
                                fillOpacity: 0.9,
                                weight: 2
                            }}
                        >
                            <Popup>
                                <div className="p-2 min-w-[180px]">
                                    <h3 className="font-semibold text-base">{vehicle.name}</h3>
                                    <p className="text-sm opacity-80">{vehicle.type} - {vehicle.plate_number}</p>
                                    <div className="mt-1.5 pt-1.5 border-t border-white/10">
                                        <p className="text-xs font-semibold" style={{ color: vehicleStatusColors[vehicle.status] }}>
                                            {vehicleStatusLabels[vehicle.status] || vehicle.status}
                                        </p>
                                        <GpsFreshness lastUpdate={vehicle.last_gps_update} className="mt-1" />
                                        {vehicle.current_mission && (
                                            <p className="text-sm mt-1">{vehicle.current_mission}</p>
                                        )}
                                        {vehicle.problem_report && (
                                            <p className="text-xs text-red-400 mt-1">{vehicle.problem_report.description}</p>
                                        )}
                                    </div>
                                    {vehicle.assigned_personnel && vehicle.assigned_personnel.length > 0 && (
                                        <div className="mt-1.5 pt-1.5 border-t border-white/10">
                                            <p className="text-xs opacity-60">Equipage :</p>
                                            {vehicle.assigned_personnel.map((p) => (
                                                <p key={p.personnel_id || p.personnel_name} className="text-xs">{p.personnel_name} ({p.role})</p>
                                            ))}
                                        </div>
                                    )}
                                    {vehicle.last_gps_update && (
                                        <p className="text-[10px] opacity-40 mt-1">MAJ: {new Date(vehicle.last_gps_update).toLocaleTimeString('fr-FR')}</p>
                                    )}
                                </div>
                            </Popup>
                        </CircleMarker>
                    ))}
                    {/* Zones dangereuses */}
                    {dzSignale.map(z => (
                        <CircleMarker key={`dz-${z.id}`} center={[z.latitude, z.longitude]} radius={14} pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.35 }}>
                            <Popup><div className="text-xs"><b>Zone dangereuse</b><br />{z.lieu}<br />{z.description}<br /><span className="opacity-60">{z.type_danger} - par {z.signale_par}</span></div></Popup>
                        </CircleMarker>
                    ))}
                    {dzSecurise.map(z => (
                        <CircleMarker key={`dzs-${z.id}`} center={[z.latitude, z.longitude]} radius={10} pathOptions={{ color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.25 }}>
                            <Popup><div className="text-xs"><b>Zone securisee</b><br />{z.lieu}<br />{z.description}</div></Popup>
                        </CircleMarker>
                    ))}
                    {/* Dessins de carte (zones precises) */}
                    <DrawingsLayer drawings={drawings} />
                </MapContainer>

                <CompactLegend hasZones={dangerZones.length > 0} />
            </div>
        </CardContent>
    </Card>
    );
};

export default SituationMapCard;
